import type { DeckProject, FillMode, VisualElement } from './types'
import { defaultVariableWidth } from './defaults'
import { createDefaultGradient, createDefaultShadow, normalizedGradientStops } from './visual-style'
import { normalizeFontWeight } from './font-assets'
import { assertDeckProject, assertProjectSource } from './project-validation'

const gradientDefaults = (source: unknown, idPrefix: string) => {
  const defaults = createDefaultGradient(idPrefix)
  const gradient = source && typeof source === 'object' ? source as Record<string, unknown> : {}
  return {
    type: gradient.type === 'radial' ? 'radial' as const : 'linear' as const,
    angle: Number(gradient.angle ?? defaults.angle),
    centerX: Number(gradient.centerX ?? defaults.centerX),
    centerY: Number(gradient.centerY ?? defaults.centerY),
    radius: Number(gradient.radius ?? defaults.radius),
    stops: normalizedGradientStops(Array.isArray(gradient.stops) ? gradient.stops as typeof defaults.stops : defaults.stops),
  }
}

const elementDefaults = (element: Record<string, unknown>) => {
  const legacyImage = element.type === 'image'
  if (legacyImage) element.type = 'rectangle'
  if (!element.fillMode) {
    const supportsFill = element.type === 'rectangle' || element.type === 'ellipse'
    element.fillMode = legacyImage ? 'image' : supportsFill && element.fill !== 'transparent' ? 'solid' : 'none'
  }
  element.imageScale ??= 1
  element.imageOffsetX ??= 0
  element.imageOffsetY ??= 0
  element.imageRotation ??= 0
  element.imageOpacity ??= 1
  element.imageFit ??= 'cover'
  element.gradient = gradientDefaults(element.gradient, `${String(element.id || 'element')}-gradient-stop`)
  element.shadow = { ...createDefaultShadow(), ...(element.shadow && typeof element.shadow === 'object' ? element.shadow as object : {}) }
  element.strokeStyle ??= 'solid'
  element.strokeDashLength ??= 2
  element.strokeDashGap ??= 1
  element.lineCap ??= 'round'
  element.lineJoin ??= 'round'
  element.startArrow ??= false
  element.endArrow ??= false
  element.polygonSides ??= 6
  element.starPoints ??= 5
  element.starInnerRatio ??= 0.48
  element.arcStartAngle ??= -90
  element.arcSweepAngle ??= element.type === 'ring' ? 360 : 120
  element.ringInnerRatio ??= 0.7
  element.fontStyle ??= 'normal'
  element.fontWeight = normalizeFontWeight(element.fontWeight)
  element.letterSpacing ??= 0
  element.lineHeight ??= 1.22
  element.textDecoration ??= 'none'
  element.textTransform ??= 'none'
  element.bindings ??= {}
  return element as unknown as VisualElement
}

export const migrateProject = (source: unknown): DeckProject => {
  if (!source || typeof source !== 'object') throw new Error('This is not a valid Deckelier project.')
  const version = Number((source as Record<string, unknown>).schemaVersion ?? 1)
  if (version !== 1 && version !== 2 && version !== 3 && version !== 4) throw new Error('This Deckelier project version is not supported.')
  assertProjectSource(source)
  const project = structuredClone(source) as Record<string, unknown>
  project.elements = (project.elements as Array<Record<string, unknown>>).map((element) => elementDefaults(element))
  const card = project.card as Record<string, unknown>
  card.borderEnabled ??= false
  card.borderColor ??= '#111111'
  card.borderWidthMm ??= 3
  card.borderInnerRadiusMm ??= 2
  card.backgroundFillMode = card.backgroundFillMode === 'gradient' ? 'gradient' : 'solid'
  card.backgroundGradient = gradientDefaults(card.backgroundGradient, 'card-background-gradient-stop')
  project.variables = (project.variables as Array<Record<string, unknown>>).map((source) => {
    const variable = source as Record<string, unknown>
    variable.columnWidthPx ??= defaultVariableWidth(variable.type as Parameters<typeof defaultVariableWidth>[0])
    return variable
  })
  project.groups = Array.isArray(project.groups) ? project.groups : []
  project.assets = project.assets && typeof project.assets === 'object' ? project.assets : {}
  project.fonts = project.fonts && typeof project.fonts === 'object' ? project.fonts : {}
  project.dataSettings = project.dataSettings && typeof project.dataSettings === 'object'
    ? project.dataSettings
    : { cardNameWidthPx: 180 }
  const render = project.render && typeof project.render === 'object'
    ? project.render as Record<string, unknown>
    : {}
  render.profile ??= 'custom'
  render.pageWidthMm ??= 210
  render.pageHeightMm ??= 297
  render.autoRotate ??= true
  project.render = render
  project.schemaVersion = 4
  const migrated = project as unknown as DeckProject
  assertDeckProject(migrated)
  return migrated
}

export const supportsImageFill = (element: VisualElement) =>
  element.type === 'rectangle' || element.type === 'ellipse' || element.type === 'polygon' || element.type === 'star' || element.type === 'ring'

export const normalizedFillMode = (element: VisualElement): FillMode =>
  supportsImageFill(element) ? element.fillMode : 'none'
